/* Adding a system, and changing or removing one — the forms, next to the picker that offers
 * them rather than on a tab of their own.
 *
 * A system is a product this assistant answers for: its key is the `iss` claim of every token
 * issued for it, the `system` column on every telemetry row it writes, and the thing an
 * operator account is granted. The field that earns these forms is the CORPUS FOLDER — the
 * documentation repository keeps one directory per system, and this is what says which one
 * belongs to which. Before it was a column that mapping was a constant compiled into two
 * codebases, so a system added at run time could only ever have the derived `<key>_system`
 * folder.
 *
 * Adding one and changing one, and nothing else. Switching a system off or deleting it are
 * lifecycle, and lifecycle belongs to the Integrations tab: a system answers through the
 * applications registered against it, so cutting those off is what stops it, and it is the
 * step that says which application is losing its users.
 *
 * Superadmin-only, and the routes refuse anybody else: an admin runs one system's documents,
 * not the list of systems. The caller decides whether to render any of this — see
 * `DocumentsPanel`, which is where the picker lives and therefore where these belong.
 */

import { useState } from 'react';
import { Button, Input, Textarea } from '@nextleap-al/admin-ui';

import { detailOf, write } from '../api/client';
import type { System } from '../api/types';
import { ResultLine } from './Panel';
import { plural, shortDate } from '../lib/format';
import { useApp } from '../state/app';

/** What the key field will accept, mirrored from `SYSTEM_KEY_RE` on the server so the form
 *  can say so before it posts. The server is still the one that decides. */
const KEY_RE = /^[a-z0-9][a-z0-9_-]{1,30}[a-z0-9]$/;

/** The folder a blank field means, which is what both services derived before the column
 *  existed. Shown as the placeholder, so leaving it empty is visibly not "no folder". */
const derivedFolder = (key: string): string => (key ? `${key}_system` : 'nextpass');

/** The explanation under the folder field, in one place: it is said when adding a system and
 *  again when changing one, and the second time it has to be a warning as well. Plain text,
 *  because `Input` renders its hint as one. */
const folderHint = (existing?: boolean): string =>
  "The folder under the corpus root holding this system's documents — lower case; letters, " +
  "digits, '-' and '_'; no slashes; " +
  (existing
    ? 'changing it leaves documents already filed under the old one where they are, no longer ' +
      'this system’s.'
    : 'leave it empty and it is derived from the key.');

/** The frame both forms sit in: set apart from the picker above it, since it is a form and
 *  the picker is a choice. */
const FORM_CLASS =
  'space-y-4 rounded-[var(--radius-md)] border border-[var(--border-light)] ' +
  'bg-[var(--bg-elevated)] p-4';

export function AddSystemForm({
  onDone,
}: {
  /** Called with the new key on success, so the picker can select what was just made, and
   *  with nothing when the form is dismissed. */
  onDone: (key?: string) => void;
}) {
  const { reload } = useApp();
  const [key, setKey] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [blurb, setBlurb] = useState('');
  const [folder, setFolder] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const keyed = key.trim().toLowerCase();
  // Said while it is being typed rather than after it is posted, since the key is the one
  // field that cannot be corrected afterwards.
  const keyProblem = keyed && !KEY_RE.test(keyed) ? 'Lower case, 3–32 characters.' : undefined;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    const { ok, data } = await write('/api/systems', {
      system_key: keyed,
      display_name: displayName.trim(),
      blurb: blurb.trim(),
      corpus_folder: folder.trim(),
    });
    setBusy(false);
    if (!ok) {
      setResult({ ok, text: detailOf(data, 'The system could not be added.') });
      return;
    }
    reload();
    onDone(keyed);
  }

  return (
    <form onSubmit={submit} className={FORM_CLASS}>
      <div className="grid gap-4 md:grid-cols-2">
        <Input
          label="System key"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          autoComplete="off"
          placeholder="e.g. nextpass"
          error={keyProblem}
          hint="The name everything else is stored under: the `iss` claim in this system's tokens, the `system` column in the analytics, and the grants on operator accounts. Lower case; cannot be changed later."
          required
        />
        <Input
          label="Display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          autoComplete="off"
          placeholder="e.g. NextPass"
          hint="What people call the product. This is what this picker and the toolbar filter show."
        />
      </div>
      <Input
        label="Corpus folder"
        value={folder}
        onChange={(e) => setFolder(e.target.value)}
        autoComplete="off"
        placeholder={derivedFolder(keyed)}
        hint={folderHint()}
      />
      <Textarea
        label="What it is (optional)"
        rows={2}
        value={blurb}
        onChange={(e) => setBlurb(e.target.value)}
        placeholder="Access and attendance — doors, shifts, leave and the people being tracked."
        hint="One line, shown under the system's name on its card above."
      />
      <div className="flex flex-wrap gap-2">
        <Button type="submit" isLoading={busy} disabled={!keyed || Boolean(keyProblem)}>
          Add system
        </Button>
        <Button type="button" variant="ghost" onClick={() => onDone()}>
          Cancel
        </Button>
      </div>
      {result && <ResultLine ok={result.ok}>{result.text}</ResultLine>}
    </form>
  );
}

export function EditSystemForm({ item, onDone }: { item: System; onDone: () => void }) {
  const { reload } = useApp();
  const [displayName, setDisplayName] = useState(item.display_name);
  const [blurb, setBlurb] = useState(item.blurb);
  const [folder, setFolder] = useState(item.corpus_folder);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const { ok, data } = await write(`/api/systems/${item.id}`, {
      display_name: displayName.trim(),
      blurb: blurb.trim(),
      corpus_folder: folder.trim(),
    });
    setBusy(false);
    if (!ok) {
      setResult({ ok, text: detailOf(data, 'That did not work.') });
      return;
    }
    reload();
    onDone();
  }

  return (
    <form onSubmit={submit} className={FORM_CLASS}>
      <div className="grid gap-4 md:grid-cols-2">
        <Input
          label="Display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />
        <Input
          label="Corpus folder"
          value={folder}
          onChange={(e) => setFolder(e.target.value)}
          placeholder={derivedFolder(item.system_key)}
          hint={folderHint(true)}
        />
      </div>
      <Textarea
        label="What it is"
        rows={2}
        value={blurb}
        onChange={(e) => setBlurb(e.target.value)}
      />
      <p className="max-w-prose text-sm text-[var(--text-muted)]">
        The key <code>{item.system_key}</code> cannot be changed: it is the <code>iss</code> in
        tokens already in flight and the <code>system</code> on every row written. Added{' '}
        {shortDate(item.created_at)}
        {item.created_by ? ` by ${item.created_by}` : ''};{' '}
        {item.integrations
          ? `${plural(item.integrations, 'integration')} registered against it.`
          : 'nothing is registered against it, so nothing can ask it a question yet.'}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" isLoading={busy}>
          Save changes
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
      </div>
      {result && <ResultLine ok={result.ok}>{result.text}</ResultLine>}
    </form>
  );
}
